import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const SKYLINE =
  "https://images.unsplash.com/photo-1513061379709-ef0cd1695189?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzbWFydCUyMGNpdHklMjBza3lsaW5lJTIwbmlnaHQlMjBhZXJpYWx8ZW58MHx8fHwxNzg3ODI3MDU1fDA&ixlib=rb-4.1.0&q=85";

const FEATURED = [
  {
    id: "tata-electronics",
    name: "Tata Electronics",
    domain: "tataelectronics.com",
    investment: "₹91,000 Cr",
    summary:
      "India's first commercial semiconductor fab — the anchor project driving the entire SIR ecosystem.",
  },
  {
    id: "reliance",
    name: "Reliance Industries",
    domain: "ril.com",
    investment: "₹7,500 Cr",
    summary:
      "A blue-chip renewable energy and logistics play that signals global-lender confidence in Dholera.",
  },
  {
    id: "tata-power",
    name: "Tata Power",
    domain: "tatapower.com",
    investment: "₹3,000 Cr",
    summary:
      "A 300 MW solar park delivering round-the-clock green power to industries and residents alike.",
  },
  {
    id: "jabil",
    name: "Jabil",
    domain: "jabil.com",
    investment: "₹2,000 Cr",
    summary:
      "A global electronics manufacturing giant setting up to serve the semiconductor supply chain.",
  },
];

export default function MajorInvestments() {
  const navigate = useNavigate();

  return (
    <section
      id="major-investments"
      className="relative overflow-hidden py-24 md:py-32"
      data-testid="major-investments-section"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${SKYLINE})` }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-[#050810]/85" aria-hidden="true" />
      <div
        className="absolute inset-0 bg-gradient-to-b from-[#050810] via-transparent to-[#050810]"
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-7xl px-5 md:px-10">
        <div className="max-w-2xl">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">
            The Anchor Tenants
          </p>
          <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-white sm:text-5xl">
            Major Investments
            <br />
            <span className="text-accent">in Dholera</span>
          </h2>
          <p className="mt-5 max-w-lg text-sm leading-relaxed text-white/60">
            ₹1.28 lakh crore of committed capital from India's largest
            industrial houses is reshaping the region — and the land beneath it.
          </p>
        </div>

        <div className="mt-14 grid gap-5 sm:grid-cols-2">
          {FEATURED.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group relative overflow-hidden rounded-sm border border-white/10 bg-white/[0.04] p-7 backdrop-blur-xl transition-colors duration-300 hover:border-accent/40 hover:bg-white/[0.07]"
              data-testid={`investment-card-${c.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-sm bg-white p-2.5">
                  <img
                    src={`https://logo.clearbit.com/${c.domain}`}
                    alt={`${c.name} logo`}
                    className="h-full w-full object-contain"
                    loading="lazy"
                    onError={(e) => {
                      const parent = e.currentTarget.parentElement;
                      if (!parent) return;
                      const words = c.name.split(" ");
                      parent.textContent = (
                        words.length > 1
                          ? words.map((w) => w[0]).join("")
                          : c.name
                      ).slice(0, 2);
                      parent.className =
                        "flex h-14 w-14 shrink-0 items-center justify-center rounded-sm bg-white font-display text-lg font-extrabold text-[#050810]";
                    }}
                  />
                </div>
                <div className="text-right">
                  <p className="font-display text-2xl font-extrabold tracking-tight text-accent md:text-3xl">
                    {c.investment}
                  </p>
                  <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40">
                    Committed
                  </p>
                </div>
              </div>
              <h3 className="mt-6 font-display text-xl font-bold text-white">
                {c.name}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-white/55">
                {c.summary}
              </p>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <button
            onClick={() => navigate("/investments")}
            className="group inline-flex items-center gap-3 bg-accent px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-accent-foreground shadow-2xl transition-colors duration-200 hover:bg-white hover:text-[#050810]"
            data-testid="view-all-investments-btn"
          >
            View All 10 Major Investments
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </button>
        </div>
      </div>
    </section>
  );
}
